'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import {
  Bell, CalendarDays, ChevronDown, Clock3, HeartHandshake, Landmark,
  Megaphone, BookOpen, Phone, MapPin, Languages, Navigation, User, Share2,
} from 'lucide-react'
import { KitCard, EmptyState, AIBadge, AIShimmer, PageHero } from '../../components/ui/kit'
import AdSlot from '../../components/ui/AdSlot'
import {
  noticeStage, formatIst, directionsUrl, whatsappShareUrl, noticeShareText,
  type NoticeMeta,
} from '../../lib/announcement-notice'
import { iconForLabel } from '../../lib/icon-map'

/* ─────────────────────────────────────────────────────────────
   ANNOUNCEMENTS & NOTICES (Phase 2) — crisp, scannable cards:
   type badge · one-line headline · masjid · time · expand for
   detail. Time-critical notices get a priority lane at the top.
   The word "Janaza" appears only inside the relevant type.
───────────────────────────────────────────────────────────── */

interface Notice {
  id: string
  cat: string
  title: string
  body: string
  masjidName: string
  masjidSlug?: string
  area?: string
  time: string
  live?: boolean
  janazaTime?: string
  graveyard?: string
  contact?: string
  photoUrl?: string | null
  eventAt?: string | null
  meta?: NoticeMeta | null
}

/* DB stores lowercase categories ('janaza','event'); demo seeds use Title-case
   ('Janaza','Notice'). Normalize to the CAT_META keys so the Janaza badge and
   the priority lane resolve regardless of source. */
const CAT_NORMALIZE: Record<string, string> = {
  janaza: 'Janaza', notice: 'Notice', event: 'Event', khutba: 'Khutba',
  lecture: 'Khutba', finance: 'Finance', program: 'Program', update: 'Update',
  urgent: 'Notice', general: 'Notice',
}
const normCat = (c?: string) => CAT_NORMALIZE[String(c || '').toLowerCase()] || (c && CAT_META[c] ? c : 'Notice')

const MASJID_SLUGS: Record<string, string> = {
  'Masjid E Noor': 'masjid-e-noor',
  'Masjid Rahmaniya': 'masjid-rahmaniya',
  'Jamia Masjid Charminar': 'jamia-masjid-charminar',
  'Masjid Bilal': 'masjid-bilal',
  'Masjid E Aqsa': 'masjid-e-aqsa-banjara',
  'Masjid Ibrahim': 'masjid-ibrahim-jubilee',
}

const CAT_META: Record<string, { Icon: any; tone: string; label: string }> = {
  Janaza: { Icon: Bell, tone: 'gold', label: 'Funeral prayer notice' },
  Notice: { Icon: Megaphone, tone: 'green', label: 'Notice' },
  Event: { Icon: CalendarDays, tone: 'green', label: 'Event' },
  Khutba: { Icon: BookOpen, tone: 'green', label: 'Khutbah' },
  Finance: { Icon: HeartHandshake, tone: 'green', label: 'Finance' },
  Program: { Icon: CalendarDays, tone: 'green', label: 'Program' },
  Update: { Icon: Megaphone, tone: 'green', label: 'Update' },
}

const FILTERS = ['All', 'Notice', 'Event', 'Janaza', 'Program', 'Finance']

const TRANSLATE_LANGS: Array<{ code: string; label: string; rtl?: boolean }> = [
  { code: 'en', label: 'English' },
  { code: 'ur', label: 'اردو', rtl: true },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
]

function NoticeCard({ n }: { n: Notice }) {
  const [open, setOpen] = useState(false)
  const [trLang, setTrLang] = useState<string | null>(null)
  const [trData, setTrData] = useState<Record<string, { title: string; body: string }>>({})
  const [trLoading, setTrLoading] = useState(false)
  const [trFailed, setTrFailed] = useState(false)
  const meta = CAT_META[n.cat] || CAT_META.Notice
  const stage = noticeStage(n.eventAt, new Date())
  const isJanaza = n.cat === 'Janaza'
  /* time-critical = janaza, or any notice with a structured event instant still
     within its priority window (event +12h, IST) */
  const priority = isJanaza || (Boolean(n.eventAt) && stage === 'priority')
  const slug = n.masjidSlug || MASJID_SLUGS[n.masjidName]

  // structured janaza/event facts (Module B), falling back to legacy demo fields
  const md = n.meta || {}
  const eventLabel = formatIst(n.eventAt)
  const dirUrl = directionsUrl({
    mapUrl: isJanaza ? md.burialMapUrl : md.eventMapUrl,
    lat: md.burialLat, lng: md.burialLng,
  })
  const shareHref = whatsappShareUrl(noticeShareText({
    title: n.title, category: n.cat, masjidName: n.masjidName, body: n.body,
    eventAt: n.eventAt, meta: n.meta,
    url: typeof window !== 'undefined' ? `${window.location.origin}/announcements` : undefined,
  }))

  /* one-tap AI translation — cached per language, original always one tap away */
  const translate = (lang: string) => {
    if (trLang === lang) { setTrLang(null); return }
    setTrFailed(false)
    if (trData[lang]) { setTrLang(lang); return }
    setTrLoading(true)
    setTrLang(lang)
    fetch('/api/ai/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: n.title, body: n.body, lang }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.title) setTrData((p) => ({ ...p, [lang]: { title: d.title, body: d.body || '' } }))
        else { setTrFailed(true); setTrLang(null) }
      })
      .catch(() => { setTrFailed(true); setTrLang(null) })
      .finally(() => setTrLoading(false))
  }

  const shown = trLang ? trData[trLang] : null
  const rtl = Boolean(trLang && TRANSLATE_LANGS.find((l) => l.code === trLang)?.rtl)

  return (
    <article className={'an-card' + (priority ? ' priority' : '')}>
      <button type="button" className="an-row" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className={'an-badge' + (priority ? ' gold' : '')}>
          <meta.Icon size={11} strokeWidth={1.5} /> {meta.label.toUpperCase()}
        </span>
        <span className="an-title">{n.title}</span>
        <span className="an-masjid">{n.masjidName}</span>
        <span className="an-time"><Clock3 size={11} strokeWidth={1.5} /> {n.time}</span>
        <ChevronDown size={15} strokeWidth={1.5} className={'an-chev' + (open ? ' open' : '')} />
      </button>
      {open && (
        <div className="an-detail">
          {n.photoUrl && (
            <div className="an-photo"><img src={n.photoUrl} alt="" loading="lazy" /></div>
          )}
          {isJanaza && (md.deceasedName || n.eventAt || md.burialGround) && (
            <div className="an-struct">
              {md.deceasedName && (
                <span className="an-struct-row"><User size={12} strokeWidth={1.5} /> Marhoom: <b>{md.deceasedName}{md.deceasedAge ? ` (${md.deceasedAge})` : ''}</b></span>
              )}
              {n.eventAt && (
                <span className="an-struct-row"><Clock3 size={12} strokeWidth={1.5} /> Funeral prayer: <b>{eventLabel}</b>{md.janazaMasjid ? ` · ${md.janazaMasjid}` : ''}</span>
              )}
              {md.burialGround && (
                <span className="an-struct-row"><MapPin size={12} strokeWidth={1.5} /> Burial: <b>{md.burialGround}</b></span>
              )}
            </div>
          )}
          {!isJanaza && n.cat === 'Event' && (n.eventAt || md.eventVenue) && (
            <div className="an-struct">
              {n.eventAt && <span className="an-struct-row"><CalendarDays size={12} strokeWidth={1.5} /> <b>{eventLabel}</b></span>}
              {md.eventVenue && <span className="an-struct-row"><MapPin size={12} strokeWidth={1.5} /> {md.eventVenue}</span>}
            </div>
          )}
          {trLoading ? (
            <div className="an-tr-loading"><AIShimmer lines={2} /></div>
          ) : shown ? (
            <div className="an-translated" dir={rtl ? 'rtl' : 'ltr'}>
              <b>{shown.title}</b>
              {shown.body && <p>{shown.body}</p>}
              <span className="an-tr-note" dir="ltr"><AIBadge label="AI TRANSLATION" /></span>
            </div>
          ) : n.body ? (
            <p>{n.body}</p>
          ) : (
            <p className="an-nobody">No further details — open the masjid page for contact information.</p>
          )}
          {(n.body || n.title) && (
            <div className="an-langs" aria-label="Translate this notice">
              <Languages size={12} strokeWidth={1.5} />
              {TRANSLATE_LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={trLang === l.code ? 'on' : ''}
                  onClick={() => translate(l.code)}
                  disabled={trLoading}
                >{l.label}</button>
              ))}
              {trFailed && <span className="an-tr-fail">Translation unavailable right now</span>}
            </div>
          )}
          {(n.janazaTime || n.graveyard || n.contact) && !n.meta && (
            <div className="an-facts">
              {n.janazaTime && <span><Clock3 size={12} strokeWidth={1.5} /> {n.janazaTime}</span>}
              {n.graveyard && <span><MapPin size={12} strokeWidth={1.5} /> {n.graveyard}</span>}
              {n.contact && <a href={`tel:${n.contact.replace(/\s/g, '')}`}><Phone size={12} strokeWidth={1.5} /> {n.contact}</a>}
            </div>
          )}
          <div className="an-actions">
            {dirUrl && (
              <a className="an-act primary" href={dirUrl} target="_blank" rel="noopener noreferrer">
                <Navigation size={12} strokeWidth={1.5} /> Directions
              </a>
            )}
            <a className="an-act" href={shareHref} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp">
              <Share2 size={12} strokeWidth={1.5} /> Share on WhatsApp
            </a>
            {slug && <Link href={`/masjids/${slug}`} className="an-open">Open {n.masjidName} →</Link>}
          </div>
        </div>
      )}
      <style jsx>{`
        .an-card{background:#fff;border:1px solid var(--f-line);border-radius:13px;box-shadow:var(--f-shadow);overflow:hidden}
        .an-card.priority{border-color:rgba(212,175,55,.45);background:linear-gradient(150deg,#FDF7E3,#fff)}
        .an-row{display:flex;align-items:center;gap:12px;width:100%;background:none;border:none;padding:12px 15px;cursor:pointer;font-family:inherit;text-align:left}
        .an-badge{display:inline-flex;align-items:center;gap:5px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;color:var(--f-green-deep);background:rgba(14,124,97,.08);border-radius:99px;padding:4px 10px;font-weight:600;flex:none}
        .an-badge.gold{color:var(--f-gold-deep);background:rgba(212,175,55,.12)}
        .an-title{font-family:var(--f-display);font-weight:600;font-size:13.5px;color:var(--f-navy);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .an-masjid{font-size:11.5px;color:var(--f-slate);flex:none;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .an-time{display:inline-flex;align-items:center;gap:4px;font-family:var(--f-mono);font-size:9.5px;color:var(--f-slate-dim);flex:none;white-space:nowrap}
        .an-card :global(.an-chev){color:var(--f-slate-dim);transition:transform .2s;flex:none}
        .an-card :global(.an-chev.open){transform:rotate(180deg)}
        .an-detail{padding:0 15px 14px;border-top:1px solid var(--f-line);margin:0 0}
        .an-detail p{font-size:12.5px;color:var(--f-slate);line-height:1.6;padding-top:11px}
        .an-detail p.an-nobody{color:var(--f-slate-dim);font-style:italic}
        .an-tr-loading{padding-top:11px}
        .an-translated{padding-top:11px}
        .an-translated b{display:block;font-family:var(--f-display);font-weight:600;font-size:13.5px;color:var(--f-navy);margin-bottom:5px}
        .an-translated p{padding-top:0}
        .an-tr-note{display:inline-block;margin-top:8px}
        .an-langs{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:12px;padding-top:10px;border-top:1px dashed var(--f-line);color:var(--f-slate-dim)}
        .an-langs button{border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-slate);font-size:10.5px;font-weight:600;padding:4px 11px;cursor:pointer;font-family:inherit;transition:all .2s}
        .an-langs button:hover{border-color:rgba(14,124,97,.35);color:var(--f-green-deep)}
        .an-langs button.on{background:rgba(14,124,97,.08);border-color:rgba(14,124,97,.35);color:var(--f-green-deep)}
        .an-langs button:disabled{opacity:.55;cursor:default}
        .an-tr-fail{font-size:10.5px;color:#B4574E;font-style:italic}
        .an-facts{display:flex;gap:14px;flex-wrap:wrap;margin-top:10px}
        .an-facts span,.an-facts :global(a){display:inline-flex;align-items:center;gap:5px;font-size:11.5px;color:var(--f-ink);font-weight:500;text-decoration:none}
        .an-facts :global(a){color:var(--f-green)}
        .an-photo{margin-top:11px;border-radius:11px;overflow:hidden;aspect-ratio:16/9;background:var(--f-line)}
        .an-photo img{width:100%;height:100%;object-fit:cover;display:block}
        .an-struct{display:grid;gap:6px;margin-top:11px;padding:11px 13px;background:rgba(14,124,97,.04);border:1px solid var(--f-line);border-radius:10px}
        .an-struct-row{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--f-slate)}
        .an-struct-row b{color:var(--f-navy);font-weight:600}
        .an-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:12px;padding-top:11px;border-top:1px dashed var(--f-line)}
        .an-detail :global(.an-act){display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;text-decoration:none;border:1px solid var(--f-line-strong);border-radius:99px;padding:6px 12px;color:var(--f-slate);background:#fff}
        .an-detail :global(.an-act.primary){color:var(--f-green-deep);border-color:rgba(14,124,97,.4);background:rgba(14,124,97,.06)}
        .an-detail :global(.an-open){margin-left:auto;font-size:11.5px;font-weight:600;color:var(--f-green);text-decoration:none}
        @media(max-width:640px){
          .an-masjid{display:none}
          .an-time{display:none}
        }
      `}</style>
    </article>
  )
}

/* shared by SSR init and the client refresh — same merge, one code path */
export type ApiNoticeRow = {
  id: string; title: string; body?: string; category?: string
  masjid_name?: string; masjidName?: string; masjid_slug?: string | null; time?: string
  photo_url?: string | null; event_at?: string | null; meta?: NoticeMeta | null
}
function buildNotices(rows: ApiNoticeRow[] | null): Notice[] {
  // H5: DEMO_ANNOUNCEMENTS seed removed — notices come ONLY from real API rows
  // (masjid_announcements). Honest-empty when none, same principle as H3/H4.
  const seeded: Notice[] = []
  const fromApi: Notice[] = Array.isArray(rows)
    ? rows.filter((r: any) => r.body || r.title).map((r: any) => ({
        id: `api-${r.id}`, cat: normCat(r.category), title: r.title,
        body: r.body || '', masjidName: r.masjid_name || r.masjidName || 'Masjid',
        masjidSlug: r.masjid_slug || undefined, time: r.time || 'Recently',
        photoUrl: r.photo_url || null, eventAt: r.event_at || null, meta: r.meta || null,
      }))
    : []
  const seen = new Set<string>()
  return [...fromApi, ...seeded].filter((n) => {
    const k = `${n.title}|${n.masjidName}`.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

export default function AnnouncementsPage({ initialRows }: { initialRows: ApiNoticeRow[] }) {
  const [notices, setNotices] = useState<Notice[]>(() => buildNotices(initialRows))
  const [filter, setFilter] = useState('All')
  const [loading, setLoading] = useState(false)

  // honor /announcements?type=Janaza deep links (the old /janaza page 308s here)
  useEffect(() => {
    try {
      const type = new URLSearchParams(window.location.search).get('type')
      if (type && FILTERS.includes(type)) setFilter(type)
    } catch {}
  }, [])

  useEffect(() => {
    let alive = true
    fetch('/api/announcements?limit=50')
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => { if (alive && Array.isArray(rows)) setNotices(buildNotices(rows)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const filtered = useMemo(
    () => notices.filter((n) => filter === 'All' || n.cat === filter),
    [notices, filter],
  )
  /* Priority lane = time-critical notices still inside their window. Structured
     notices use the IST stage (event +12h); legacy demo janaza without a
     structured instant fall back to the live flag. Archived (event +48h) rows
     are already filtered out server-side. */
  const inLane = (n: Notice) => {
    if (n.eventAt) return noticeStage(n.eventAt, new Date()) === 'priority'
    return n.cat === 'Janaza' && n.live !== false
  }
  const priorityLane = filtered.filter(inLane)
  const rest = filtered.filter((n) => !priorityLane.includes(n))

  return (
    <main className="anp">
      <PageHero
        compact
        motif="community"
        eyebrow="Announcements &amp; notices"
        title={<>Every notice, scannable in seconds.</>}
        subtitle="Committee announcements, events and time-critical notices from masjids across the network."
      />

      <div className="anp-wrap anp-body">
        <div className="anp-filters" role="toolbar" aria-label="Notice types">
          {FILTERS.map((f) => {
            const Icon = f === 'All' ? Megaphone : (iconForLabel(f) || Megaphone)
            return (
              <button key={f} type="button" className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
                <Icon size={13} strokeWidth={1.6} /> {f === 'Janaza' ? 'Funeral prayer' : f}
              </button>
            )
          })}
        </div>

        {loading ? (
          <div className="anp-skeletons">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="anp-skeleton" />)}</div>
        ) : (
          <>
            {priorityLane.length > 0 && (
              <section className="anp-lane" aria-label="Time-critical notices">
                <div className="anp-lane-head"><Bell size={13} strokeWidth={1.5} /> PRIORITY LANE — TIME-CRITICAL</div>
                <div className="anp-list">
                  {priorityLane.map((n) => <NoticeCard key={n.id} n={n} />)}
                </div>
              </section>
            )}

            {rest.length ? (
              <div className="anp-list">
                {rest.flatMap((n, idx) => [
                  /* FS2-D: ONE slot between regular notices — never the priority lane */
                  ...(idx === 3 ? [<AdSlot key="ad-announcements" placement="announcements_list" variant="native" />] : []),
                  <NoticeCard key={n.id} n={n} />,
                ])}
              </div>
            ) : !priorityLane.length ? (
              <EmptyState
                icon={<Megaphone size={20} strokeWidth={1.5} />}
                title="No notices in this category yet"
                body="Committee announcements appear here the moment they're posted. Committees post from MasjidOS."
                cta={{ label: 'Open MasjidOS', href: '/dashboard/announcements' }}
              />
            ) : null}
          </>
        )}

        <KitCard className="anp-cta">
          <Landmark size={16} strokeWidth={1.5} />
          <div>
            <b>Run a masjid?</b>
            <span>Post notices, events and time-critical alerts to your followers from MasjidOS.</span>
          </div>
          <Link href="/dashboard/announcements">Post a notice →</Link>
        </KitCard>
      </div>

      <style jsx>{`
        .anp{background:var(--f-beige);}
        .anp-wrap{max-width:980px;margin:0 auto;padding:0 26px}
        .anp-hero{position:relative;z-index:0;padding:42px 0 34px}
        .anp-hero-bg{position:absolute;left:0;right:0;top:0;bottom:-110px;z-index:-1;pointer-events:none;
          background:url('/hero-art/signal.svg') right -70px bottom -20px / 520px auto no-repeat,radial-gradient(560px 320px at 18% 30%, rgba(14,124,97,.14), transparent 62%),linear-gradient(170deg,#101A38,#0B132B);
          -webkit-mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%);
          mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%)}
        .anp-pattern{position:absolute;inset:0;opacity:.04;background-image:radial-gradient(rgba(255,255,255,.85) 1px,transparent 1.5px);background-size:26px 26px}
        .anp-eyebrow{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-mono);font-size:10px;letter-spacing:.18em;color:#14B8A6;border:1px solid rgba(20,184,166,.35);border-radius:99px;padding:4px 12px;background:rgba(20,184,166,.08);margin-bottom:12px}
        .anp-hero h1{font-family:var(--f-display);font-size:clamp(24px,2.8vw,34px);font-weight:700;color:#F6F4EE;letter-spacing:-.01em}
        .anp-hero p{color:rgba(246,244,238,.75);font-size:14px;margin-top:8px;max-width:560px}

        .anp-body{padding-bottom:48px}
        .anp-filters{display:flex;gap:7px;margin:18px 0 16px;overflow-x:auto;scrollbar-width:none}
        .anp-filters::-webkit-scrollbar{display:none}
        .anp-filters button{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--f-line-strong);border-radius:99px;background:#fff;color:var(--f-slate);font-size:12px;font-weight:600;padding:7px 14px;cursor:pointer;font-family:inherit;white-space:nowrap;transition:all .2s}
        .anp-filters button.on{background:var(--f-navy);border-color:var(--f-navy);color:#fff}

        .anp-lane{margin-bottom:18px}
        .anp-lane-head{display:flex;align-items:center;gap:7px;font-family:var(--f-mono);font-size:9.5px;letter-spacing:.14em;color:var(--f-gold-deep);margin-bottom:9px;font-weight:600}
        .anp-list{display:grid;gap:8px}

        .anp-skeletons{display:grid;gap:8px}
        .anp-skeleton{height:46px;border-radius:13px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:ansk 1.4s linear infinite}
        @keyframes ansk{from{background-position:200% 0}to{background-position:-200% 0}}

        .anp :global(.anp-cta){display:flex;align-items:center;gap:12px;margin-top:22px;color:var(--f-green)}
        .anp :global(.anp-cta b){display:block;font-family:var(--f-display);font-weight:600;font-size:13.5px;color:var(--f-navy)}
        .anp :global(.anp-cta span){font-size:12px;color:var(--f-slate)}
        .anp :global(.anp-cta > a){margin-left:auto;font-size:12px;font-weight:600;color:var(--f-green);text-decoration:none;white-space:nowrap}

        @media(prefers-reduced-motion:reduce){.anp-skeleton{animation:none}}
      `}</style>
    </main>
  )
}
